/**
 * The ONE place an order (or order-line) status is named, labelled and toned (DNKADM-0442).
 *
 * `status` is an OPEN-ENDED wire string: dink-api adds values without a migration here
 * (DNKAPI-0634 added `NEEDS_REVIEW`). The parser keeps the raw string and never rejects
 * one; everything that SHOWS a status goes through these helpers so that
 *  - a known value gets its label, tone and help text,
 *  - an unknown value renders as its raw text in a neutral badge, and never throws.
 *
 * `NEEDS_REVIEW` means money was taken (the card tender is `SUCCEEDED`) and nothing was
 * applied: staff must decide refund or grant. It is NOT reversible through the order
 * reversal endpoints (`OrderReversalService.requireReversible` refuses it), and it must
 * never be described as declined, failed or "nothing was charged".
 */

export const ORDER_STATUSES = [
  'COMPLETED',
  'PENDING',
  'FAILED',
  'REFUNDED',
  'VOIDED',
  'NEEDS_REVIEW',
  'SCHEDULED',
  'PARTIALLY_REFUNDED',
] as const;

export type KnownOrderStatus = (typeof ORDER_STATUSES)[number];

/** A known status, or any other string the API sends (kept verbatim). */
export type OrderStatus = KnownOrderStatus | (string & {});

export type OrderStatusTone = 'success' | 'warning' | 'error' | 'neutral';

export const NEEDS_REVIEW_STATUS: KnownOrderStatus = 'NEEDS_REVIEW';

/** Help text for NEEDS_REVIEW, shown as a tooltip on the badge and under the receipt status. */
export const NEEDS_REVIEW_HELP = 'Payment taken, nothing applied. Refund it or apply it.';

const LABELS: Record<KnownOrderStatus, string> = {
  COMPLETED: 'Completed',
  PENDING: 'Pending',
  FAILED: 'Failed',
  REFUNDED: 'Refunded',
  VOIDED: 'Voided',
  NEEDS_REVIEW: 'Needs review',
  SCHEDULED: 'Scheduled',
  PARTIALLY_REFUNDED: 'Partially refunded',
};

const TONES: Record<KnownOrderStatus, OrderStatusTone> = {
  COMPLETED: 'success',
  PENDING: 'warning',
  FAILED: 'error',
  REFUNDED: 'neutral',
  VOIDED: 'neutral',
  NEEDS_REVIEW: 'warning',
  SCHEDULED: 'neutral',
  PARTIALLY_REFUNDED: 'neutral',
};

function isKnown(status: string): status is KnownOrderStatus {
  return Object.prototype.hasOwnProperty.call(LABELS, status);
}

/** Label for a status; an unknown value is returned as sent (raw), never throws. */
export function orderStatusLabel(status: OrderStatus | null | undefined): string {
  if (status === null || status === undefined || status === '') return 'Unknown';
  return isKnown(status) ? LABELS[status] : status;
}

/** Badge tone; an unknown value is `neutral`. */
export function orderStatusTone(status: OrderStatus | null | undefined): OrderStatusTone {
  if (status === null || status === undefined) return 'neutral';
  return isKnown(status) ? TONES[status] : 'neutral';
}

/** Tooltip/help text, or null when the status has none. */
export function orderStatusHelp(status: OrderStatus | null | undefined): string | null {
  return status === NEEDS_REVIEW_STATUS ? NEEDS_REVIEW_HELP : null;
}

export function isNeedsReview(status: OrderStatus | null | undefined): boolean {
  return status === NEEDS_REVIEW_STATUS;
}

/**
 * Staff-facing sentence for an order that did not settle to COMPLETED inside a sale flow.
 * `noMoneyClaim` is what the caller says about money for the FAILED/other cases; a
 * NEEDS_REVIEW order ALWAYS says the payment was taken.
 */
export function unsettledOrderMessage(
  status: OrderStatus,
  label: string | number,
  copy: { pending: string; failed: string },
): string {
  const ref = String(label);
  if (status === 'PENDING') return copy.pending.replace('{order}', ref).trim();
  if (status === NEEDS_REVIEW_STATUS) {
    return `Order ${ref} needs review. The payment was taken but nothing was applied. Refund it or apply it.`.trim();
  }
  if (status === 'FAILED') return copy.failed.replace('{order}', ref).trim();
  return `Order ${ref} ended as ${orderStatusLabel(status)}. Check the order before trying again.`.trim();
}
